import React, { useState, useEffect } from 'react';
import { BrandLogo } from '../components/BrandLogo';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, MapPin, Calendar, Play, Share2, Copy, Check, ExternalLink,
  Linkedin, MessageCircle, Mail, ArrowLeft, Eye, Clock, Tag, Video,
  CalendarDays, Rocket, Users, Award, Flag, Star, Globe, Images
} from 'lucide-react';
import PhotoGalleryPlayer from '../components/PhotoGalleryPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event type icons
const EVENT_TYPE_ICONS = {
  trade_show: CalendarDays,
  visit: Building2,
  launch: Rocket,
  conference: Users,
  webinar: Video,
  award: Award,
  milestone: Flag,
  other: Star
};

const EVENT_TYPE_LABELS = {
  trade_show: "Trade Show",
  visit: "Factory/Office Visit",
  launch: "Product Launch",
  conference: "Conference",
  webinar: "Webinar",
  award: "Award/Recognition",
  milestone: "Company Milestone",
  other: "Event"
};

// Extract YouTube video ID
function getYouTubeId(url) {
  if (!url) return null;
  // Handle direct ID
  if (url.length === 11 && !url.includes('/')) return url;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
}

// Video player component
function ShowcaseVideoPlayer({ video, isHero = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const youtubeId = getYouTubeId(video?.youtube_id || video?.url);
  
  if (!youtubeId) return null;
  
  return (
    <div className={`relative rounded-xl overflow-hidden bg-zinc-900 border-2 border-zinc-700/80 shadow-2xl shadow-black/50 ring-1 ring-white/10 ${isHero ? 'aspect-video' : 'aspect-video'}`}>
      {!isPlaying ? (
        <div 
          className="relative w-full h-full cursor-pointer group"
          onClick={() => setIsPlaying(true)}
        >
          <img 
            src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
            alt={video.title || 'Video'}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
            }}
          />
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#C41E3A] flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
              <Play className="w-8 h-8 md:w-10 md:h-10 text-white fill-white ml-1" />
            </div>
          </div>
          {video.title && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
              <p className="text-white font-medium">{video.title}</p>
              {video.type && (
                <p className="text-zinc-400 text-sm">{video.type}</p>
              )}
            </div>
          )}
        </div>
      ) : (
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`}
          title={video.title || 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      )}
    </div>
  );
}

// Share button component
function ShareButtons({ url, title, companyName, onShare }) {
  const [copied, setCopied] = useState(false);
  
  const shareText = `Check out ${title} by ${companyName}`;
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    onShare?.('copy');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleShare = (platform) => {
    onShare?.(platform);
    let shareUrl = '';
    switch (platform) {
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText + '\n\n' + url)}`;
        break;
      default:
        return;
    }
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-zinc-500 text-sm mr-2">Share:</span>
      <button
        onClick={() => handleShare('linkedin')}
        className="p-2 rounded-lg bg-[#0077B5]/20 hover:bg-[#0077B5]/30 text-[#0077B5] transition-colors"
        title="Share on LinkedIn"
      >
        <Linkedin className="w-5 h-5" />
      </button>
      <button
        onClick={() => handleShare('whatsapp')}
        className="p-2 rounded-lg bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] transition-colors"
        title="Share on WhatsApp"
      >
        <MessageCircle className="w-5 h-5" />
      </button>
      <button
        onClick={() => handleShare('email')}
        className="p-2 rounded-lg bg-zinc-700/50 hover:bg-zinc-600/50 text-zinc-300 transition-colors"
        title="Share via Email"
      >
        <Mail className="w-5 h-5" />
      </button>
      <button
        onClick={handleCopy}
        className="p-2 rounded-lg bg-zinc-700/50 hover:bg-zinc-600/50 text-zinc-300 transition-colors"
        title="Copy Link"
      >
        {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
      </button>
    </div>
  );
}

export default function ShowcaseEventPage() {
  const { slug, eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchEvent();
  }, [slug, eventId]);
  
  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/p/${slug}/showcase/${eventId}`);
      setEvent(response.data.event);
      setProfile(response.data.profile);
    } catch (err) {
      console.error('Error fetching event:', err);
      setError(err.response?.data?.detail || 'Event not found');
    } finally {
      setLoading(false);
    }
  };
  
  const handleShare = async (platform) => {
    try {
      await axios.post(`${API_URL}/api/profiles/${profile.id}/showcase/${eventId}/share?platform=${platform}`);
    } catch (err) {
      console.error('Error tracking share:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C41E3A]"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Event Not Found</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link to="/" className="text-[#C41E3A] hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }
  
  const EventIcon = EVENT_TYPE_ICONS[event.event_type] || Star;
  const eventTypeLabel = EVENT_TYPE_LABELS[event.event_type] || "Event";
  const shareUrl = window.location.href;
  
  // Check if this is a gallery event
  const isGalleryEvent = event.event_format === 'gallery';
  
  // For video events: Find hero video
  const heroVideo = event.videos?.find(v => v.youtube_id === event.hero_video_id) || event.videos?.[0];
  const otherVideos = event.videos?.filter(v => v !== heroVideo) || [];
  
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to={`/p/${profile.slug}`} 
            className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to {profile.company_name}</span>
          </Link>
          <ShareButtons 
            url={shareUrl} 
            title={event.title} 
            companyName={profile.company_name}
            onShare={handleShare}
          />
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Company Badge */}
        <div className="flex items-center gap-3 mb-6">
          {profile.logo_url ? (
            <img src={profile.logo_url} alt={profile.company_name} className="w-12 h-12 rounded-lg object-contain bg-white p-1" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-zinc-500" />
            </div>
          )}
          <div>
            <Link to={`/p/${profile.slug}`} className="text-white font-semibold hover:text-[#C41E3A] transition-colors">
              {profile.company_name}
            </Link>
            {profile.tagline && (
              <p className="text-zinc-500 text-sm">{profile.tagline}</p>
            )}
          </div>
        </div>
        
        {/* Event Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C41E3A]/20 border border-[#C41E3A]/30 text-[#C41E3A] text-sm">
              <EventIcon className="w-4 h-4" />
              {eventTypeLabel}
            </span>
            {event.visibility === 'hidden' && (
              <span className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm">
                Unlisted
              </span>
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{event.title}</h1>
          
          <div className="flex flex-wrap items-center gap-4 text-zinc-400">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{new Date(event.event_date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
            {event.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{event.location}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span>{event.views || 0} views</span>
            </div>
          </div>
        </div>
        
        {/* Hero Content - Video or Photo Gallery */}
        {isGalleryEvent ? (
          /* Photo Gallery Player */
          event.gallery_images?.length > 0 && (
            <div className="mb-8">
              <PhotoGalleryPlayer
                images={event.gallery_images}
                settings={event.gallery_settings}
                title={event.title}
                profileName={profile.company_name}
                profileSlug={profile.slug}
                profileLogo={profile.logo_url}
                autoPlay={true}
                showControls={true}
              />
              {/* Gallery info bar */}
              <div className="mt-3 flex items-center justify-between text-sm text-zinc-500">
                <div className="flex items-center gap-2">
                  <Images className="w-4 h-4" />
                  <span>{event.gallery_images.length} photos</span>
                </div>
                {event.gallery_settings?.music_track && (
                  <div className="flex items-center gap-2">
                    <span>♪ Background music enabled</span>
                  </div>
                )}
              </div>
            </div>
          )
        ) : (
          /* Video Player */
          heroVideo && (
            <div className="mb-8">
              <ShowcaseVideoPlayer video={heroVideo} isHero />
            </div>
          )
        )}
        
        {/* Description */}
        {event.description && (
          <div className="mb-8 bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <p className="text-zinc-300 whitespace-pre-wrap">{event.description}</p>
          </div>
        )}
        
        {/* Additional Videos - only for video events */}
        {!isGalleryEvent && otherVideos.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">More Videos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherVideos.map((video, index) => (
                <ShowcaseVideoPlayer key={index} video={video} />
              ))}
            </div>
          </div>
        )}
        
        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div className="mb-8 flex items-center gap-2 flex-wrap">
            <Tag className="w-4 h-4 text-zinc-500" />
            {event.tags.map((tag, index) => (
              <span key={index} className="px-3 py-1 rounded-full bg-zinc-800 text-zinc-400 text-sm">
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {/* CTA */}
        <div className="bg-gradient-to-r from-[#C41E3A]/20 to-zinc-900/50 border border-[#C41E3A]/30 rounded-xl p-6 text-center">
          <h3 className="text-xl font-semibold text-white mb-2">Want to learn more about {profile.company_name}?</h3>
          <p className="text-zinc-400 mb-4">Visit our full profile to explore our products, services, and more.</p>
          <Link 
            to={`/p/${profile.slug}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#C41E3A] hover:bg-[#A01830] text-white font-semibold rounded-lg transition-colors"
          >
            View Full Profile
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">
            Powered by <Link to="/" className="inline-block align-middle ml-1"><BrandLogo className="h-6" /></Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
